import { ENV } from '../config/env';
import { supabase } from './supabaseClient';

const NOTIFY_PHONE = '7007543565';
const NOTIFY_URL = `${ENV.SUPABASE_URL}/functions/v1/app-enquiry-notify`;
const WHATSAPP_SEND_URL = `${ENV.SUPABASE_URL}/functions/v1/whatsapp-send`;
const DEDUP_KEY = 'rs_app_enquiry_wa_sent_v2';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  apikey: ENV.SUPABASE_ANON_KEY,
  Authorization: `Bearer ${ENV.SUPABASE_ANON_KEY}`,
});

function getWhatsAppUserId() {
  try {
    const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
    if (user?.id && String(user.id).length > 20) return user.id;
  } catch {
    // ignore
  }
  return ENV.WHATSAPP_SYSTEM_USER_ID || null;
}

function fmtInr(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `₹${n.toLocaleString('en-IN')}`;
}

function fmtDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function parseGarageNotes(raw) {
  if (!raw || typeof raw !== 'string') return {};
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

function wasAlreadyNotified(type, id) {
  try {
    const sent = JSON.parse(localStorage.getItem(DEDUP_KEY) || '[]');
    return sent.includes(`${type}:${id}`);
  } catch {
    return false;
  }
}

function markNotified(type, id) {
  try {
    const sent = JSON.parse(localStorage.getItem(DEDUP_KEY) || '[]');
    const key = `${type}:${id}`;
    if (!sent.includes(key)) {
      sent.push(key);
      localStorage.setItem(DEDUP_KEY, JSON.stringify(sent.slice(-500)));
    }
  } catch {
    // ignore
  }
}

export function buildAppEnquiryWhatsAppMessage(type, row = {}) {
  if (type === 'car_loan') {
    return [
      '🚗 New Car Loan Enquiry',
      '',
      `Customer: ${row.customer_name || '—'}`,
      `Phone: ${row.customer_phone || row.mobile || '—'}`,
      `Email: ${row.customer_email || '—'}`,
      `PAN: ${row.pan_number || '—'}`,
      `Vehicle: ${row.vehicle_registration || '—'}`,
      `Monthly Income: ${fmtInr(row.monthly_income)}`,
      `Occupation: ${row.occupation || '—'}`,
      `Status: ${row.status || 'submitted'}`,
      `Submitted: ${fmtDate(row.created_at)}`,
    ].join('\n');
  }

  if (type === 'car_service') {
    const meta = parseGarageNotes(row.notes);
    const lines = [
      '🔧 New Car Service Booking',
      '',
      `Customer: ${row.customer_name || '—'}`,
      `Phone: ${row.customer_phone || '—'}`,
      `Email: ${row.customer_email || '—'}`,
      `Vehicle: ${row.vehicle_registration || '—'}`,
    ];
    if (row.vehicle_brand || row.vehicle_model) {
      lines.push(`Car: ${[row.vehicle_brand, row.vehicle_model].filter(Boolean).join(' ')}`);
    }
    if (row.vehicle_fuel_type) lines.push(`Fuel: ${row.vehicle_fuel_type}`);
    lines.push(
      `Category: ${row.service_name || row.service_type || '—'}`,
      `Package: ${row.package_name || row.package_key || meta.package_key || '—'}`,
    );
    if (row.package_points) lines.push(`Points: ${row.package_points}`);
    if (row.package_oil_type) lines.push(`Oil: ${row.package_oil_type}`);
    lines.push(
      `City: ${row.booking_city || meta.city || '—'}`,
      `Slot: ${row.pickup_slot_label || meta.pickup_slot_label || '—'}`,
      `Pickup: ${fmtDate(row.booking_date)}`,
      `Amount: ${fmtInr(row.amount_paid ?? row.amount ?? meta.amount_paid)}`,
    );
    if (row.coupon_code || meta.coupon_code) lines.push(`Coupon: ${row.coupon_code || meta.coupon_code}`);
    if (row.razorpay_payment_id || meta.razorpay_payment_id) {
      lines.push(`Payment ID: ${row.razorpay_payment_id || meta.razorpay_payment_id}`);
    }
    lines.push(`Status: ${row.status || '—'}`, `Booked: ${fmtDate(row.created_at)}`);
    return lines.join('\n');
  }

  const gps = row.pickup_latitude && row.pickup_longitude
    ? `${row.pickup_latitude}, ${row.pickup_longitude}`
    : null;
  const mapsUrl = gps ? `https://www.google.com/maps?q=${row.pickup_latitude},${row.pickup_longitude}` : null;
  const lines = [
    '🛡️ New RSA Request',
    '',
    `Customer: ${row.customer_name || '—'}`,
    `Phone: ${row.customer_phone || row.contact_number || '—'}`,
    `Email: ${row.customer_email || '—'}`,
    `Service: ${row.service_type || '—'}`,
    `Vehicle: ${row.vehicle_registration || row.vehicle_number || '—'}`,
  ];
  if (row.vehicle_brand || row.vehicle_model) {
    lines.push(`Car: ${[row.vehicle_brand, row.vehicle_model].filter(Boolean).join(' ')}`);
  }
  if (row.membership_plan_name) lines.push(`Membership: ${row.membership_plan_name}`);
  if (row.membership_card_number) lines.push(`Card: ${row.membership_card_number}`);
  if (gps) lines.push(`GPS: ${gps}`);
  if (mapsUrl) lines.push(`Maps: ${mapsUrl}`);
  lines.push(`Address: ${row.pickup_address || '—'}`);
  lines.push(
    `Request Status: ${row.request_status || '—'}`,
    `Complaint Status: ${row.complaint_status || '—'}`,
    `Submitted: ${fmtDate(row.complaint_registered_at || row.requested_at || row.created_at)}`,
  );
  return lines.join('\n');
}

async function postWhatsApp(payload) {
  const res = await fetch(WHATSAPP_SEND_URL, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return res.json();
}

function normalizeNotifyPhone(phone = NOTIFY_PHONE) {
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length === 10) return `91${digits}`;
  return digits;
}

/** True when customer replied to business WhatsApp in last 24 hours. */
async function hasActiveWhatsAppWindow(phone = NOTIFY_PHONE) {
  try {
    const contactNumber = normalizeNotifyPhone(phone);
    const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const { data, error } = await supabase
      .from('whatsapp_messages')
      .select('id')
      .eq('contact_number', contactNumber)
      .eq('direction', 'inbound')
      .gte('created_at', since)
      .limit(1);
    if (error) return false;
    return (data?.length || 0) > 0;
  } catch {
    return false;
  }
}

/**
 * Send exactly ONE WhatsApp alert:
 * - 24h window open → full enquiry text only
 * - otherwise → approved template only (never both)
 */
async function sendWhatsAppDirect(type, row) {
  const userId = getWhatsAppUserId();
  if (!userId) {
    return { success: false, error: 'No admin user id for WhatsApp send' };
  }

  const content = buildAppEnquiryWhatsAppMessage(type, row);
  const base = {
    to: NOTIFY_PHONE,
    contact_name: 'App Enquiries Alert',
    user_id: userId,
  };

  const windowOpen = await hasActiveWhatsAppWindow();

  if (windowOpen) {
    return postWhatsApp({ ...base, type: 'text', content });
  }

  const detailTemplate = ENV.APP_ENQUIRY_WA_DETAIL_TEMPLATE;
  if (detailTemplate) {
    const detailResult = await postWhatsApp({
      ...base,
      type: 'template',
      template_name: detailTemplate,
      template_language: ENV.APP_ENQUIRY_WA_TEMPLATE_LANG || 'en',
      template_params: [content.slice(0, 900)],
      content,
    });
    if (detailResult?.success) return detailResult;
  }

  return postWhatsApp({
    ...base,
    type: 'template',
    template_name: ENV.APP_ENQUIRY_WA_TEMPLATE || 'service_booking_update',
    template_language: ENV.APP_ENQUIRY_WA_TEMPLATE_LANG || 'en',
    template_params: [],
    content,
  });
}

async function notifyViaEdgeFunction(type, id) {
  const res = await fetch(NOTIFY_URL, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ type, id }),
  });
  return res.json();
}

/** Send instant WhatsApp alert (same pipeline as /whatsapp page). */
export async function notifyAppEnquiryWhatsApp(type, id, row = null) {
  if (!type || !id) return { success: false, error: 'Missing type or id' };
  if (wasAlreadyNotified(type, id)) {
    return { success: true, skipped: true, reason: 'already_sent_local' };
  }

  try {
    let result;

    if (row) {
      result = await sendWhatsAppDirect(type, row);
    } else {
      result = await notifyViaEdgeFunction(type, id);
    }

    if (result?.success || result?.skipped) {
      markNotified(type, id);
    } else {
      console.warn('notifyAppEnquiryWhatsApp failed:', result?.error || result);
    }
    return result;
  } catch (error) {
    console.error('notifyAppEnquiryWhatsApp:', error);
    return { success: false, error: error.message };
  }
}

export async function notifyAppEnquiriesBatch(items) {
  await Promise.all(
    items.map(({ type, id, row }) => notifyAppEnquiryWhatsApp(type, id, row)),
  );
}
